import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

message.config({
  duration: 0,
  maxCount: 10,
  getContainer: () => document.body,
});

const NAVY = '#075dc5';

type ToastIconColor = {
  bg: string;
  fg: string;
  accent: string;
  border: string;
};

const SUCCESS_COLORS: ToastIconColor = {
  bg: '#dcfce7',
  fg: '#16a34a',
  accent: '#16a34a',
  border: '#bbf7d0',
};

const ERROR_COLORS: ToastIconColor = {
  bg: '#fee2e2',
  fg: '#dc2626',
  accent: '#dc2626',
  border: '#fecaca',
};

const WARNING_COLORS: ToastIconColor = {
  bg: '#fef3c7',
  fg: '#d97706',
  accent: '#d97706',
  border: '#fde68a',
};

const INFO_COLORS: ToastIconColor = {
  bg: '#dbeafe',
  fg: NAVY,
  accent: NAVY,
  border: '#bfdbfe',
};

let baseStylesInjected = false;
const injectBaseStyles = () => {
  if (baseStylesInjected) return;
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes doc-toast-progress {
      from { width: 100%; }
      to { width: 0%; }
    }

    .ant-message .ant-message-notice .ant-message-notice-content {
      background: transparent !important;
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      display: block !important;
    }

    .ant-message .ant-message-notice-content .ant-message-custom-content {
      display: block !important;
    }

    .ant-message .ant-message-notice-content .ant-message-custom-content > .anticon {
      display: none !important;
    }

    .ant-message .ant-message-notice {
      margin-bottom: 12px !important;
      padding: 0 !important;
      text-align: center !important;
      display: flex !important;
      justify-content: center !important;
      pointer-events: auto !important;
    }

    .ant-message {
      top: auto !important;
      right: 24px !important;
      bottom: 24px !important;
      left: auto !important;
      width: max-content !important;
      max-width: calc(100vw - 48px);
      pointer-events: none !important;
    }

    .ant-message .ant-message-notice {
      justify-content: flex-end !important;
    }

    @media (max-width: 480px) {
      .ant-message {
        right: 12px !important;
        bottom: 12px !important;
        max-width: calc(100vw - 24px);
      }
    }
  `;
  document.head.appendChild(style);
  baseStylesInjected = true;
};

type ToastContentProps = {
  title: string;
  description: string | undefined;
  Icon: React.FC<any>;
  colors: ToastIconColor;
  duration: number;
  toastKey: string | number;
};

const ToastContent: React.FC<ToastContentProps> = ({
  title,
  description,
  Icon,
  colors,
  duration,
  toastKey,
}) => {
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    injectBaseStyles();
  }, []);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      message.destroy(toastKey);
    }, duration);
    return () => {
      clearTimer();
      message.destroy(toastKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    clearTimer();
    message.destroy(toastKey);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px',
        minWidth: '340px',
        maxWidth: '460px',
        background: '#ffffff',
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 14px 36px rgba(15, 23, 42, 0.14), 0 5px 14px rgba(15, 23, 42, 0.07)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: colors.accent,
        }}
      />

      <div
        style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: colors.bg,
          color: colors.fg,

          marginLeft: '8px',
          alignSelf: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={18} strokeWidth={2.4} />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: description && description.trim() ? '2px' : '0',
          flex: 1,

          paddingRight: '2px',
          minWidth: 0,
          textAlign: 'center',
          alignSelf: 'center',
        }}
      >
        <span
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#0f172a',
            lineHeight: 1.35,
            textAlign: 'center',
          }}
        >
          {title}
        </span>
        {description && description.trim() && (
          <span
            style={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#475569',
              lineHeight: 1.45,
              textAlign: 'center',
            }}
          >
            {description}
          </span>
        )}
      </div>

      <button
        onClick={handleClose}
        aria-label="Close notification"
        style={{
          width: '28px',
          height: '28px',
          minWidth: '28px',
          borderRadius: '7px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          alignSelf: 'center',
          outline: 'none',
          cursor: 'pointer',
          background: 'transparent',
          color: '#64748b',
          transition: 'background 0.15s ease, color 0.15s ease',
          padding: 0,
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9';
          (e.currentTarget as HTMLButtonElement).style.color = '#0f172a';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = '#64748b';
        }}
      >
        <X size={16} strokeWidth={2.2} />
      </button>

      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          right: 0,
          height: '3px',
          background: '#f1f5f9',
          overflow: 'hidden',
          borderRadius: '0 0 12px 12px',
          marginTop: '12px',
        }}
      >
        <div
          style={{
            height: '100%',
            background: colors.accent,
            animation: `doc-toast-progress ${duration}ms linear forwards`,
            animationPlayState: 'running',
            borderRadius: '0 0 12px 0',
          }}
        />
      </div>
    </div>
  );
};

const splitMessage = (msg: string): { title: string; description?: string } => {
  if (!msg) {
    return { title: 'Notification' };
  }
  const firstBreak = msg.search(/[.!?]\s+/);
  const firstNewline = msg.indexOf('\n');
  let splitIdx = -1;
  if (firstBreak !== -1 && firstNewline !== -1) {
    splitIdx = Math.min(firstBreak + 1, firstNewline);
  } else if (firstBreak !== -1) {
    splitIdx = firstBreak + 1;
  } else if (firstNewline !== -1) {
    splitIdx = firstNewline;
  }
  if (splitIdx > 0 && splitIdx < msg.length - 2 && msg.length > 48) {
    return {
      title: msg.slice(0, splitIdx).trim(),
      description: msg.slice(splitIdx).trim(),
    };
  }
  return { title: msg };
};

let toastCounter = 0;
const nextKey = () => `doc-toast-${++toastCounter}`;

const toastSuccess = (msg = 'Operation completed successfully') => {
  injectBaseStyles();
  const { title, description } = splitMessage(msg);
  const duration = 2200;
  const key = nextKey();
  message.open({
    key,
    duration: 0,
    content: (
      <ToastContent
        title={title}
        description={description}
        Icon={CheckCircle2}
        colors={SUCCESS_COLORS}
        duration={duration}
        toastKey={key}
      />
    ),
    icon: <span />,
    className: 'doc-toast doc-toast-success',
  });
};

const toastError = (msg = 'Something went wrong. Please try again.') => {
  injectBaseStyles();
  const { title, description } = splitMessage(msg);
  const duration = 2600;
  const key = nextKey();
  message.open({
    key,
    duration: 0,
    content: (
      <ToastContent
        title={title}
        description={description}
        Icon={XCircle}
        colors={ERROR_COLORS}
        duration={duration}
        toastKey={key}
      />
    ),
    icon: <span />,
    className: 'doc-toast doc-toast-error',
  });
};

const toastWarning = (msg = 'Please review before proceeding') => {
  injectBaseStyles();
  const { title, description } = splitMessage(msg);
  const duration = 2600;
  const key = nextKey();
  message.open({
    key,
    duration: 0,
    content: (
      <ToastContent
        title={title}
        description={description}
        Icon={AlertTriangle}
        colors={WARNING_COLORS}
        duration={duration}
        toastKey={key}
      />
    ),
    icon: <span />,
    className: 'doc-toast doc-toast-warning',
  });
};

const toastInfo = (msg = 'Information') => {
  injectBaseStyles();
  const { title, description } = splitMessage(msg);
  const duration = 2400;
  const key = nextKey();
  message.open({
    key,
    duration: 0,
    content: (
      <ToastContent
        title={title}
        description={description}
        Icon={Info}
        colors={INFO_COLORS}
        duration={duration}
        toastKey={key}
      />
    ),
    icon: <span />,
    className: 'doc-toast doc-toast-info',
  });
};

const toastEarning = toastWarning;

export { toastSuccess, toastError, toastWarning, toastEarning, toastInfo };
